import React, { useRef } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { formatPrizeMoney, shortGroup } from "@/utils/race-cards";
import { formatTimeOfDay } from "@/utils/time-format";
import { ChevronRight } from "lucide-react";
import ResultsTally from "@/components/ResultsTally";
import AbandonedLabel from "@/components/abandoned-label";
import FlameIcon from "@/components/icons/flame-icon";
import TickerText from "@/components/ui/ticker-text";

/**
 * Schedule filter chips (Extra Details on): All races / $200k+ / Group & Listed.
 * @param {string} filter - "all" | "big" | "group"
 * @param {Function} onChange - called with the chosen filter
 * @param {Object} counts - { big, group } races on today's tracks
 */
export function RaceFilterChips({ filter, onChange, counts }) {
  const chips = [
    { id: "all", label: "All races" },
    { id: "big", label: `$200k+ (${counts?.big ?? 0})` },
    { id: "group", label: `Group & Listed (${counts?.group ?? 0})` },
  ];
  return (
    <div
      role="group"
      aria-label="Filter races"
      className="race-filter-chips flex flex-wrap gap-2 px-4 pb-1"
    >
      {chips.map((chip) => {
        const active = filter === chip.id;
        return (
          <button
            key={chip.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chip.id)}
            className={cn(
              "h-10 rounded-full border px-4 text-[13px] font-semibold transition-colors",
              active
                ? "border-[#F30099] bg-[#F30099] text-white"
                : "border-gray-200 bg-white text-gray-900 hover:border-gray-300",
            )}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The races that pass the filter, across all of today's tracks, in jump
 * order. Rows match the Schedule's race rows: Top Tip and the results tally
 * stay on the right; the Group, prize money and race name sit under the race.
 * @param {Array} races - from filterRacesByCard: [{ track, race, card }]
 */
export function RaceFilterList({
  filter,
  races,
  historicalData,
  onSelect,
  isRaceAbandoned = () => false,
  getAbandonmentReason = () => "",
}) {
  const countLine =
    filter === "group"
      ? `${races.length} Group and Listed ${races.length === 1 ? "race" : "races"} today, in jump order`
      : `${races.length} ${races.length === 1 ? "race" : "races"} worth $200k or more today, in jump order`;
  const todayKey = format(new Date(), "dd-MM-yyyy");
  // Race-name tickers by row: the row is the button, so its tap resets them
  const tickers = useRef({});

  if (races.length === 0) {
    return (
      <div className="race-filter-empty px-4 py-6 text-center text-sm text-gray-600">
        {filter === "group"
          ? "No Group or Listed races today."
          : "No $200k+ races today."}
      </div>
    );
  }

  return (
    <div className="race-filter-list space-y-2">
      <p className="px-4 text-xs text-gray-600">{countLine}</p>
      {races.map(({ track, race, card }) => {
        const raceKey = `${track.name.replace(/\s+/g, "-")}-${race.number}-${todayKey}`;
        const results = historicalData?.[raceKey];
        const podium = Array.isArray(results?.podium) ? results.podium : [];
        const isCompleted = podium.length > 0;
        const hasWins = podium.includes("winnerWinnerChickenDinner");
        const hasPlaces = podium.includes("PL");
        const isAbandoned = isRaceAbandoned(track.name, race.number);

        const start = race.data?.marketStartTime
          ? new Date(race.data.marketStartTime)
          : null;
        const isPast = start && start < new Date();

        const topTipId = race.data?.firstFourData?.[0]?.selectionId;
        const winnerId = results?.betfairResults?.winner?.selectionId;
        const isHotTip = !!winnerId && topTipId === winnerId;

        const prize = formatPrizeMoney(card.prizeMoney);
        const select = () => {
          tickers.current[raceKey]?.restart();
          onSelect(track, race);
        };

        return (
          <div
            key={`${track.name}-${race.number}`}
            role="button"
            tabIndex={0}
            onClick={select}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                select();
              }
            }}
            className={cn(
              "race-button-container relative mx-4 cursor-pointer rounded-lg bg-white p-4 shadow",
              isCompleted && "race-completed",
              hasWins && "race-results-win",
              hasPlaces && "race-results-place",
              isAbandoned && "race-abandoned bg-gray-200",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">
                    {track.name} R{race.number}
                  </span>
                  {start && (
                    <span
                      className={`whitespace-nowrap rounded px-2 py-0.5 text-xs ${isPast ? "text-gray-500" : "text-deepPink"}`}
                    >
                      {formatTimeOfDay(start)}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-600">
                  {card.group && (
                    <span className="flex-none rounded-md bg-[#FFE6F6] px-1.5 py-px text-[11px] font-bold text-[#C8007E]">
                      {shortGroup(card.group)}
                    </span>
                  )}
                  {prize && (
                    <span className="flex-none font-semibold tabular-nums text-gray-900">
                      {prize}
                    </span>
                  )}
                  {card.name && (
                    <>
                      <span aria-hidden="true" className="flex-none">
                        ·
                      </span>
                      <TickerText
                        ref={(t) => {
                          if (t) tickers.current[raceKey] = t;
                          else delete tickers.current[raceKey];
                        }}
                        text={card.name}
                        interactive={false}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-none items-center space-x-2">
                {race.isTopTip && (
                  <div className="flex items-center rounded-full bg-[#FFEBBB] px-2 py-0.5">
                    <FlameIcon className="h-4 w-4" />
                    <span className="ml-1 text-xs font-bold text-[#A0200C]">
                      {isHotTip ? "Hot Tip" : "Top Tip"}
                    </span>
                  </div>
                )}
                <ResultsTally
                  historicalData={historicalData}
                  raceKey={raceKey}
                  iconSize="w-5 h-5"
                  textSize="text-sm"
                  className="mr-1"
                  tipCount={2}
                />
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </div>

            {isAbandoned && (
              <div className="abandoned-indicator absolute inset-0 flex items-center justify-center font-semibold">
                <AbandonedLabel
                  className="px-3 py-1 text-sm"
                  reason={getAbandonmentReason(track.name)}
                  showReason={true}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
